import type { Container } from "pixi.js";
import type { GridLandReaction, PresentationTaskRunner } from "@slot/presentation";
import type { ScatterLightTuning } from "../tuning/scatterLightTuning";

type ColumnLanding = Parameters<GridLandReaction["onColumnLanded"]>[0];

/**
 * The light at the cannon's mouth on a scatter drop (user, 2026-10-15): whenever a landed column holds one of
 * `triggerSymbolIds`, `light` flares — it fades in while growing into its scale, holds, and fades out, hidden after. A
 * new drop restarts it from the top, a generation token retiring the flare in flight. Timing runs on the presentation
 * runner outside any scope, as the grid's land bounce does: turbo, pause and frame-step scale it, and a slam, which
 * finishes scopes, never cuts it short.
 */
export class ScatterDropLight implements GridLandReaction {
  private readonly light: Container;
  private readonly runner: PresentationTaskRunner;
  private readonly triggerSymbolIds: ReadonlySet<string>;
  private readonly tuning: ScatterLightTuning;
  private generation = 0;

  public constructor(input: { light: Container; runner: PresentationTaskRunner; triggerSymbolIds: ReadonlySet<string>; tuning: ScatterLightTuning }) {
    this.light = input.light;
    this.runner = input.runner;
    this.triggerSymbolIds = input.triggerSymbolIds;
    this.tuning = input.tuning;
  }

  public onColumnLanded(landing: ColumnLanding): void {
    if (this.light.destroyed) return;
    if (!landing.symbolIds.some((id) => this.triggerSymbolIds.has(id))) return;
    void this.flare();
  }

  private async flare(): Promise<void> {
    const { light, runner, tuning } = this;
    const generation = ++this.generation;
    const live = (): boolean => generation === this.generation && !light.destroyed;
    const show = (alpha: number, scale: number): void => {
      light.alpha = alpha;
      light.scale.set(scale);
    };

    show(0, tuning.scale * tuning.fromScale);
    light.visible = true;
    await runner.tweenProgress(Math.max(0.001, tuning.inSeconds), (k) => { if (live()) show(tuning.peakAlpha * k, tuning.scale * (tuning.fromScale + (1 - tuning.fromScale) * k)); }, undefined, { easing: tuning.inEasing });
    if (!live()) return;
    if (tuning.holdSeconds > 0) await runner.sleep(tuning.holdSeconds);
    if (!live()) return;
    await runner.tweenProgress(Math.max(0.001, tuning.outSeconds), (k) => { if (live()) show(tuning.peakAlpha * (1 - k), tuning.scale); }, undefined, { easing: tuning.outEasing });
    if (!live()) return;
    light.visible = false;
  }
}
